/**
 * lazyWithRetry — 배포로 옛 chunk 가 사라진 탭에서의 ChunkLoadError 자동 복구
 * ===========================================================================
 * 배경 (2026-08-04 PROD 장애):
 *   사용자가 탭을 열어둔 채로 새 프론트 빌드가 배포되면, 이미 로드된 SPA 는
 *   옛 해시 chunk(`6282.896dfcf9.chunk.js`)를 계속 요청한다. 새 빌드에는 그 파일이
 *   없으므로 404 → `ChunkLoadError: Loading chunk 6282 failed.` → Suspense 아래에서
 *   throw → ErrorBoundary("OOPS!") 로 낙하. 사용자는 원인을 알 수 없고, 강력 새로고침
 *   전까지 그 화면에 영영 못 들어간다. (실측: main.a0f17922.js 가 PROD 에서 404,
 *   당일 배포된 현재 번들은 main.5d6e2d49.js)
 *
 * 정책:
 *   1) 1회 재시도 — 일시적 네트워크 실패/순간 단절이면 여기서 복구된다.
 *   2) 그래도 chunk 에러면 "새 배포로 옛 파일이 사라진 것" 으로 판단하고 새로고침한다.
 *      새 index.html → 새 번들 해시를 받으므로 정상 복구된다.
 *   3) 새로고침은 **세션 내 최대 RELOAD_MAX(2)회** 로 제한한다. 서버가 실제로 깨져 chunk 가
 *      계속 404 인 상황에서 무한 리로드 루프에 빠지지 않게 하기 위함이다.
 *      다만 마지막 자동 새로고침으로부터 RELOAD_COUNT_RESET_MS(10분) 이상 조용하면 카운트를
 *      리셋하므로, 엄밀히는 «생애 총량 상한» 이 아니라 «2회/10분 레이트리밋» 이다.
 *      상한에 걸리면 자동 복구를 포기하고 ErrorBoundary 의 안내 화면으로 넘긴다.
 *
 * 주의: sessionStorage 를 못 쓰는 환경(사파리 프라이빗 등)에서는 리로드 이력을 남길 수
 *      없어 루프를 막을 방법이 없다. 그 경우 자동 리로드를 포기(false 반환)하고
 *      사용자에게 안내만 한다 — 조용한 무한 새로고침보다 안전하다.
 */

import React, { lazy } from 'react';

/** 자동 새로고침 시각 기록 키 (sessionStorage) */
const RELOAD_MARK_KEY = 'chunk_reload_at';
const RELOAD_COUNT_KEY = 'chunk_reload_count';

/**
 * 자동 새로고침 상한 (세션 내 연속).
 * ⚠️ 시간창이 아니라 **횟수**로 막는다. 시간창(옛 30초)만 두면, 청크 요청이 webpack 기본 타임아웃
 *    (120초)으로 실패하는 느린 회선에서 실패 판정(1차+재시도 ≈ 240초)이 창을 넘겨버려
 *    매번 "가드 만료" 상태가 되고, 결국 새로고침이 무한 반복된다. 횟수는 시간과 무관하게 멈춘다.
 */
export const RELOAD_MAX = 2;

/**
 * 마지막 자동 새로고침 이후 이만큼 조용했으면 카운트를 리셋한다.
 * 하루 종일 열어둔 탭에서 나중에 또 배포가 나가면 그때는 다시 자동 복구되어야 하기 때문.
 * 위 240초 시나리오보다 충분히 길게 잡아 연속 실패는 리셋되지 않게 한다.
 */
export const RELOAD_COUNT_RESET_MS = 10 * 60_000;

/**
 * chunk/동적 import 로딩 실패인지 판별.
 * webpack(CRA) 은 `ChunkLoadError`, Vite/브라우저 네이티브는 문구가 달라 모두 커버한다.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown; code?: unknown };
  const name = String(e.name ?? '');
  const message = String(e.message ?? '');
  if (name === 'ChunkLoadError' || String(e.code ?? '') === 'CSS_CHUNK_LOAD_FAILED') return true;
  return (
    /loading chunk\s+\S+\s+failed/i.test(message) ||
    /loading css chunk\s+\S+\s+failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

/**
 * 새 배포로 판단하고 새로고침한다. 세션 내 최대 RELOAD_MAX 회까지만 시도한다.
 * @returns 실제로 새로고침을 시작했으면 true, 가드에 막혔으면 false
 */
export function reloadForNewDeploy(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(RELOAD_MARK_KEY) || 0);
    let count = Number(window.sessionStorage.getItem(RELOAD_COUNT_KEY) || 0);
    if (!Number.isFinite(count) || count < 0) count = 0;

    // 충분히 조용했다면 "이전 배포 때의 복구" 로 보고 카운트를 새로 센다
    if (last && now - last > RELOAD_COUNT_RESET_MS) count = 0;

    // 시간이 얼마나 흘렀든 상한을 넘으면 자동 복구를 포기하고 안내 화면으로 넘긴다
    if (count >= RELOAD_MAX) return false;

    window.sessionStorage.setItem(RELOAD_MARK_KEY, String(now));
    window.sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  } catch {
    return false; // 이력을 남길 수 없으면 자동 리로드 포기 (무한 루프 방지)
  }
  window.location.reload();
  return true;
}

/** 테스트·수동 복구용 — 가드 기록 삭제 (수동 새로고침은 사용자의 명시적 의사이므로 카운트도 푼다) */
export function clearReloadMark(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RELOAD_MARK_KEY);
    window.sessionStorage.removeItem(RELOAD_COUNT_KEY);
  } catch {
    /* noop */
  }
}

/**
 * 재시도·자동 새로고침이 붙은 모듈 로더 (lazyWithRetry 의 알맹이 — 단위 테스트 대상).
 * chunk 에러가 아닌 오류(컴포넌트 모듈 자체의 예외 등)는 그대로 던져 ErrorBoundary 가 처리한다.
 */
export async function loadWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  try {
    return await factory();
  } catch (firstError) {
    if (!isChunkLoadError(firstError)) throw firstError;

    try {
      return await factory(); // ① 일시적 실패 대비 1회 재시도
    } catch (retryError) {
      if (!isChunkLoadError(retryError)) throw retryError;

      // ② 새 배포로 옛 chunk 가 사라진 상황 → 자동 새로고침 (상한 RELOAD_MAX)
      if (reloadForNewDeploy()) {
        // 새로고침이 진행되는 동안 에러 화면이 깜빡이지 않도록 영구 pending 유지
        // (Suspense fallback 이 그대로 보인 채 페이지가 교체된다)
        return await new Promise<{ default: T }>(() => {});
      }
      throw retryError; // 이미 새로고침했는데도 실패 → ErrorBoundary 안내로
    }
  }
}

/**
 * `React.lazy` 드롭인 대체. chunk 로딩 실패 시 재시도 → 자동 새로고침으로 복구한다.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() => loadWithRetry(factory));
}

export default lazyWithRetry;
